import { Box, Divider, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import { ControlResult, NlDMSTypeOfDiscrepancy } from '@e-origin/shared';

import { useDeclarant } from '@hooks/declarant';
import { retrieveCodes } from '@hooks/retrieveCodes';
import classes from './ControlResultDetails.module.css';

interface ControlResultProps {
  controlResult: ControlResult[];
  showRfiTitle?: boolean;
}

const renderField = (label: string, value?: string | number) =>
  value && (
    <div>
      <label>{label}</label>
      <p>{value}</p>
    </div>
  );

const TYPE_OF_DISCREPANCIES_LABELS: Record<NlDMSTypeOfDiscrepancy, string> = {
  [NlDMSTypeOfDiscrepancy.USE_OBSERVED]: 'Use Observed',
  [NlDMSTypeOfDiscrepancy.USE_CORRECTED]: 'Use Corrected',
  [NlDMSTypeOfDiscrepancy.NEW_SEAL]: 'New Seal',
  [NlDMSTypeOfDiscrepancy.DELETE_ELEMENT]: 'Delete Element',
  [NlDMSTypeOfDiscrepancy.OTHER_DISCREPANCY]: 'Other discrepancy (e.g. used in case of splitting a goods item)',
};

export const ControlResultDetailsDMS: React.FC<ControlResultProps> = ({ controlResult, showRfiTitle }) => {
  const { data: declarant } = useDeclarant();

  const [controlCodeLabels, setControlCodeLabels] = useState<Map<string, string>>(new Map());

  const overallControlItems = useMemo(() => controlResult.filter((result) => !result.resultsOfControl.length), [controlResult]);

  useEffect(() => {
    if (!declarant?.language) return;

    retrieveCodes(['ControlResult', 'ControlType'], '', declarant.language).then((codes) => {
      const labels = new Map(
        (codes || [])
          .filter(({ value, label }) => value && label)
          .map(({ value, label }) => {
            const [, description] = label.split(' - ');
            return [value, description || label];
          }),
      );

      setControlCodeLabels(labels);
    });
  }, [declarant?.language]);

  const getControlCodeLabel = (code?: string | number) => {
    if (!code) return;

    const codeValue = String(code);
    return controlCodeLabels.get(codeValue) || 'N/A';
  };

  return (
    <>
      <Divider mb="md" />

      <Stack mt="md">
        {overallControlItems.length > 0 && (
          <Stack gap="sm" mb="xl">
            <Text className={classes.sectionTitle}>Overall control</Text>
            {controlResult
              .filter((result) => !result.resultsOfControl?.length)
              .map((item, index) => (
                <Box mt={15} ml={20} key={`control-result-${index}`}>
                  <Box className={classes.row}>
                    {renderField('Date', item.date)}
                    {renderField('Control code', item.code)}
                    {renderField('Description', getControlCodeLabel(item.code))}
                    {renderField('Remarks', item.remarks)}
                  </Box>
                </Box>
              ))}
          </Stack>
        )}
        <Stack gap="sm">
          <Text className={classes.sectionTitle}>{showRfiTitle ? 'RFI - Enquiry for additional information' : 'Detailed control data'}</Text>
          {controlResult
            .filter((result) => result.resultsOfControl?.length)
            .map((item, index) => (
              <Stack mb={15} ml={20} key={`control-result-${index}`}>
                {(item.resultsOfControl || []).map((result, resultIndex) => (
                  <Stack mt={10} ml={20} key={`result-of-control-${index}-${resultIndex}`}>
                    <Box className={classes.row}>
                      {result.goodsItemNumber && <Box className={classes.row}>{renderField('Item number', result.goodsItemNumber)}</Box>}
                      {renderField('Risk area code', result.riskAreaCode)}
                      {renderField('Control type', result.controlType)}
                      {renderField('Description', getControlCodeLabel(result.controlType))}
                      {renderField('Control date', result.controlDate)}
                      {renderField('Remarks', result.remarks)}
                    </Box>
                    {(result.controlDetails || []).map((controlDetail, controlDetailIndex) => (
                      <Stack mt={10} ml={20} key={`control-result-detail-${index}-${resultIndex}-${controlDetailIndex}`}>
                        <Text className={classes.subSectionTitle}>Control Detail</Text>
                        <Box className={classes.row}>
                          {renderField(
                            'Action type',
                            `${controlDetail.typeOfDiscrepancies} - ${TYPE_OF_DISCREPANCIES_LABELS[controlDetail.typeOfDiscrepancies]}`,
                          )}
                          {renderField('Attribute pointer', controlDetail.attributePointer)}
                          {renderField('Corrected value', controlDetail.correctedValue)}
                          {renderField('Remarks', controlDetail.remarks)}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                ))}
              </Stack>
            ))}
        </Stack>
      </Stack>
    </>
  );
};
